/**
 * useTraversal - Manages depth-first tree navigation with conditional expansion
 * 
 * Navigation rules per architecture:
 * - Yes: go to first child, else next sibling, else bubble up
 * - No: skip children, next sibling, else bubble up  
 * - Add to cart: skip children, collect descendants, next sibling, else bubble up
 */

import { TreeNode } from '../types';

export interface TraversalResult {
    node: TreeNode | null;
    pathStack: number[];
    isComplete: boolean;
}

/**
 * Collect all descendant IDs of a node (for add-to-cart)
 */
export function collectDescendantIds(node: TreeNode): string[] {
    const ids: string[] = [];
    
    function traverse(n: TreeNode) {
        n.children.forEach(child => {
            ids.push(child.id);
            traverse(child);
        });
    }
    
    traverse(node);
    return ids;
}

/**
 * Get the node at a given path in the tree
 */
export function getNodeAtPath(tree: TreeNode[], pathStack: number[]): TreeNode | null {
    if (pathStack.length === 0 || tree.length === 0) return null;
    
    let current: TreeNode = tree[pathStack[0]];
    if (!current) return null;
    
    for (let i = 1; i < pathStack.length; i++) {
        const childIndex = pathStack[i];
        if (!current.children || childIndex >= current.children.length) {
            return null;
        }
        current = current.children[childIndex];
    }
    
    return current;
}

/**
 * Calculate the next node after answering YES
 * - If has children: go to first child
 * - Else: next sibling or bubble up
 */
export function getNextAfterYes(
    tree: TreeNode[], 
    currentPath: number[]
): TraversalResult {
    const currentNode = getNodeAtPath(tree, currentPath);
    
    if (!currentNode) {
        return { node: null, pathStack: [], isComplete: true };
    }
    
    // If has children, go to first child
    if (currentNode.children && currentNode.children.length > 0) {
        const newPath = [...currentPath, 0];
        return {
            node: currentNode.children[0],
            pathStack: newPath,
            isComplete: false
        };
    }
    
    // No children: find next sibling or bubble up
    return findNextSiblingOrBubbleUp(tree, currentPath);
}

/**
 * Calculate the next node after answering NO or ADD-TO-CART
 * - Skip children
 * - Go to next sibling or bubble up
 */
export function getNextAfterNoOrCart(
    tree: TreeNode[], 
    currentPath: number[]
): TraversalResult {
    return findNextSiblingOrBubbleUp(tree, currentPath);
}

/**
 * Find next sibling, or bubble up to find an ancestor's next sibling
 */
function findNextSiblingOrBubbleUp(
    tree: TreeNode[], 
    currentPath: number[]
): TraversalResult {
    const path = [...currentPath];
    
    while (path.length > 0) {
        const currentIndex = path[path.length - 1];
        
        // Get parent's children array
        let siblings: TreeNode[];
        if (path.length === 1) {
            siblings = tree;
        } else {
            const parentPath = path.slice(0, -1);
            const parent = getNodeAtPath(tree, parentPath);
            siblings = parent?.children || [];
        }
        
        // Check if there's a next sibling
        if (currentIndex + 1 < siblings.length) {
            const newPath = [...path.slice(0, -1), currentIndex + 1];
            const nextNode = getNodeAtPath(tree, newPath);
            return {
                node: nextNode,
                pathStack: newPath,
                isComplete: false
            };
        }
        
        // No next sibling, bubble up
        path.pop();
    }
    
    // No more nodes - assessment complete
    return { node: null, pathStack: [], isComplete: true };
}

/**
 * Count remaining questions reachable from a given position.
 * Simulates the "fastest path" (answering no/cart to everything)
 * so remaining = current node + all subsequent siblings up the stack.
 */
export function countRemainingFromPath(tree: TreeNode[], pathStack: number[]): number {
    if (pathStack.length === 0) return 0;

    let remaining = 1; // the current node itself
    let path = [...pathStack];

    while (path.length > 0) {
        const idx = path[path.length - 1];
        const siblings = path.length === 1
            ? tree
            : (getNodeAtPath(tree, path.slice(0, -1))?.children ?? []);

        remaining += siblings.length - idx - 1;
        path.pop();
    }

    return remaining;
}

/**
 * Get the initial traversal state
 */
export function getInitialTraversal(tree: TreeNode[]): TraversalResult {
    if (tree.length === 0) {
        return { node: null, pathStack: [], isComplete: true };
    }
    return {
        node: tree[0],
        pathStack: [0],
        isComplete: false
    };
}
